import arLocale from "date-fns/locale/ar-SA";
import deLocale from "date-fns/locale/de";
import esLocale from "date-fns/locale/es";
import frLocale from "date-fns/locale/fr";
import huLocale from "date-fns/locale/hu";
import itLocale from "date-fns/locale/it";
import jaLocale from "date-fns/locale/ja";
import koLocale from "date-fns/locale/ko";
import ptLocale from "date-fns/locale/pt";
import zhCNLocale from "date-fns/locale/zh-CN";
import zhTWLocale from "date-fns/locale/zh-TW";
import { RMLocation, RMLocationElement, RMMarker } from "./types";

export const currentYear = new Date().getFullYear();

// We define the list of locales here so that we can reduce bundle size by tree-shaking
export function getLocale(language: string) {
  // correct locales are like "en" or "en-US", so anything bigger than that should be cleaned (customer name)
  const cleanedLanguage =
    language.length > 5 ? language.split("-")[0] : language;
  switch (cleanedLanguage) {
    case "ar":
      return arLocale;
    case "de":
      return deLocale;
    case "es":
      return esLocale;
    case "fr":
      return frLocale;
    case "hu":
      return huLocale;
    case "it":
      return itLocale;
    case "ja":
      return jaLocale;
    case "ko":
      return koLocale;
    case "pt":
      return ptLocale;
    case "zh-CN":
      return zhCNLocale;
    case "zh-TW":
      return zhTWLocale;
    default:
      return null;
  }
}

export function convertLocationToMarker(location: RMLocation): RMMarker {
  if (!location) return null;
  return {
    name: getTranslatedLabel(location.city),
    portName: getTranslatedLabel(location.port),
    code: location.port.code,
    latLng: location.port.latLng,
    location,
    type: 0,
  };
}

export function getTranslatedLabel(element: RMLocationElement, lang?: string) {
  if (!element) return null;
  if (!lang) {
    return Object.values(element.translations)[0];
  } else {
    return element.translations[lang];
  }
}

export function getAirportSuggestionLabel(suggestion: RMLocation) {
  if (!suggestion) return null;
  const code = suggestion.port.type === "M" ? "" : ` (${suggestion.port.code})`;
  const label = getAirportLabel(suggestion);
  if (label.length === 3) {
    // we only have the airport code
    return label;
  } else {
    return `${label}${code}`;
  }
}

export function getAirportLabel(suggestion: RMLocation): string {
  if (!suggestion) return null;
  const code = suggestion.port.type === "M" ? "" : suggestion.port.code;
  const city = getTranslatedLabel(suggestion.city);
  let port = getTranslatedLabel(suggestion.port);
  if (port && city && port.toLowerCase().startsWith(city.toLowerCase())) {
    port = port.substring(city.length, port.length);
  }
  if (city && port) {
    return `${city}, ${port}`;
  } else if (city && !port) {
    return city;
  } else if (!city && port) {
    return port;
  } else {
    return code;
  }
}

export function getLocalizedRouteLabel(
  originLocation: RMLocation,
  destinationLocation: RMLocation,
  via1City?: string,
  via1Port?: string,
  via2City?: string,
  via2Port?: string
): string {
  if (!originLocation || !destinationLocation) return null;

  const formatLocation = (location: RMLocation): string => {
    const city = getTranslatedLabel(location.city);
    let port = getTranslatedLabel(location.port);

    // Remove city name from port if it's a duplicate
    if (port && city && port.toLowerCase().startsWith(city.toLowerCase())) {
      port = port.substring(city.length, port.length).trim();
    }

    if (city && port) {
      return `${city}（${port}）`;
    } else if (city) {
      return city;
    } else if (port) {
      return port;
    } else {
      return location.port.code;
    }
  };

  const formatViaLocation = (city: string, port: string): string => {
    if (!city && !port) return null;

    // Remove city name from port if it's a duplicate
    let cleanPort = port;
    if (port && city && port.toLowerCase().startsWith(city.toLowerCase())) {
      cleanPort = port.substring(city.length, port.length).trim();
    }

    if (city && cleanPort) {
      return `${city}（${cleanPort}）`;
    } else if (city) {
      return city;
    } else if (port) {
      return port;
    }
    return null;
  };

  const origin = formatLocation(originLocation);
  const destination = formatLocation(destinationLocation);
  const parts = [origin];

  // Add via airports if they exist
  if (via1City || via1Port) {
    const via1 = formatViaLocation(via1City, via1Port);
    if (via1) parts.push(via1);
  }

  if (via2City || via2Port) {
    const via2 = formatViaLocation(via2City, via2Port);
    if (via2) parts.push(via2);
  }

  parts.push(destination);
  return parts.join("-");
}
